import dayjs from "dayjs";
import { hasUserAccess } from "../backend/auth";
import { ACTIVITY_STATUS } from "../components/common/Constants";
import { USER_PRIVILEGES } from "../components/pages/UserManagement/Roles/RoleConfiguration";
import { ACTIVITY_TYPE } from "./constants";
import { AUDIT_STATUS } from "./auditStateMachine";

export function preventDefault(event: any) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
}

export function getValue(obj: any, key: string, type?: any) {
  if (!obj || !key) {
    return null
  }
  let value = { ...obj };
  const keys = (key || '').split('.');
  keys.forEach(_key => {
    value = (value || {})[_key];
  });
  if (type === 'BOOLEAN') {
    return value === true ? 'Yes' : (value === false ? 'No' : '')
  }
  return value;
}

export function filterData(obj: any, _search: string, keys: any[]) {
  _search = _search.toLowerCase();
  return Boolean(keys.find(x => {
    return getValue(obj, x).toLowerCase().includes(_search);
  }));
}

export function MaskEmail(email: string) {
  return (email || '').replace(/^(.)(.*)(.@.*)$/,
    (_, a, b, c) => a + b.replace(/./g, '*') + c
  );
}

export function parseS3Url(s3Url: string) {
  try {
    const url = new URL(s3Url);
    if (url.hostname.includes('.s3.') && url.hostname.includes('.amazonaws.com')) {
      const hostParts = url.hostname.split('.');
      const s3_bucket = hostParts[0];
      const s3_region = hostParts[2];
      const s3_key = url.pathname.substring(1);
      
      return { s3_region, s3_bucket, s3_key };
    }
    
    if (url.hostname.includes('s3.') && url.hostname.includes('.amazonaws.com')) {
      const pathParts = url.pathname.split('/');
      const s3_bucket = pathParts[1];
      const s3_key = pathParts.slice(2).join('/');
      const hostParts = url.hostname.split('.');
      const s3_region = hostParts[1];
      
      return { s3_region, s3_bucket, s3_key };
    }
  } catch (error) {
    console.error('Error parsing S3 URL:', error);
  }
  return null;
}

export async function downloadS3FileSecurely(fileName: string, s3Url: string) {
  try {
    const s3Params = parseS3Url(s3Url);
    if (!s3Params) {
      download(fileName, s3Url);
      return;
    }

    const { downloadS3File } = await import('../backend/shared');
    const response = await downloadS3File(s3Params);

    if (response.data) {
      // Create blob URL from the response data
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      download(fileName, url);
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
    } else {
      throw new Error('No file data received');
    }
  } catch (error) {
    console.error('Error downloading S3 file securely:', error);
    download(fileName, s3Url);
  }
}

// Keep the old function name for backward compatibility
export const downloadWithPresignedUrl = downloadS3FileSecurely;

export function download(fileName: string, filePath: string) {
  if (fileName && filePath) {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    link.target = "_blank"
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export function downloadFileContent({ name, type, content }: any) {
  const blob = new Blob([content], { type })
  const URL = window.URL || window.webkitURL;
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function reduceArraytoObj(arr: any[], key = 'columnName', value = 'value') {
  if ((arr || []).length === 0) {
    return {}
  }
  const _obj: any = {};
  (arr || []).forEach(obj => {
    _obj[obj[key]] = obj[value];
  });
  return _obj;
}

export function humanReadableFileSize(size: number) {
  var i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return ((size / Math.pow(1024, i)).toFixed(2) as any) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

export function humanReadableNumber(number: number) {
  // var i = number === 0 ? 0 : Math.floor(Math.log(number) / Math.log(1000));
  // return (number / Math.pow(1000, i)).toFixed(2) * 1 + ' ' + ['', 'K'][i];
  return number < 1000 ? number : `${number}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function getMinMonthYear() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 5);
  date.setMonth(3);
  date.setDate(1);
  return date;
}

// export function getMaxMonthYear() {
//   const date = new Date();
//   const month = date.getMonth();
//   if (month > 2) {
//     date.setFullYear(date.getFullYear() + 10);
//   }
//   date.setMonth(11);
//   date.setDate(31);
//   return date;
// }
export function getMaxMonthYear() {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 5); // Allows selection up to 5 years in the future
  date.setMonth(11); // December
  date.setDate(31);
  return date;
}

export function checkVendorActivityStatus(activity: any, auditStatus: any) {
  if (!hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_UPLOAD)) {
    return { editable: false }
  }

  if (activity.published) {
    return {
      editable: false
    }
  }

  const dueDate = new Date(activity.due_date);
  dueDate.setHours(23);
  dueDate.setMinutes(59);
  dueDate.setSeconds(59);
  dueDate.setMilliseconds(999);

  if (activity.auditted !== ACTIVITY_TYPE.AUDIT) {
    return {
      editable: false,
      type: 'warning',
      message: `This activity is for ${activity.auditted}. You are not allowed to edit this activity.`
    }
  }

  if (activity.status === ACTIVITY_STATUS.OVERDUE) {
    return {
      editable: false,
      type: 'danger',
      message: `This activity is expired and cannot be edited further. Contact Admin to extend the due date.`
    }
  }

  // Check audit status using state machine
  // Vendor can edit when audit status is: preliminary_audit_pending, final_audit_pending, or final_audit_editing
  if (auditStatus) {
    const allowedAuditStatuses = [
      AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING
    ];

    if (allowedAuditStatuses.includes(auditStatus)) {
      return {
        editable: true
      }
    }

    const notAllowedAuditStatuses = [
      AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
      AUDIT_STATUS.FINAL_AUDIT_SUBMITTED
    ];
  
    if (notAllowedAuditStatuses.includes(auditStatus)) {
      return {
        editable: false
      }
    }
  }

  // Check individual activity status
  if (activity.status === ACTIVITY_STATUS.PENDING) {
    return { editable: true };
  }

  if (activity.status === ACTIVITY_STATUS.ACTIVITY_SAVED) {
    if (new Date() < dueDate) {
      return { editable: true }
    } else {
      return {
        editable: false,
        type: 'warning',
        message: 'This activity is expired. No further edits allowed.'
      }
    }
  }

  if (activity.status === ACTIVITY_STATUS.SUBMITTED) {
    return {
      editable: false,
      type: 'success',
      message: 'Congratulations! This activity has been successfully submitted for auditing.'
    }
  }

  if (activity.status === ACTIVITY_STATUS.AUDITED) {
    return {
      editable: false,
      type: 'success',
      message: 'Congatulations! This activity is Approved.'
    }
  }

  if (activity.status === ACTIVITY_STATUS.REJECTED) {
    if (activity.lock) {
      return {
        editable: false,
        type: 'danger',
        message: 'Oops! This activity is rejected for not meeting the expected compliance.'
      }
    } else {
      if(auditStatus && [AUDIT_STATUS.FINAL_AUDIT_EDITING].includes(auditStatus)){
        return {
          editable: false,
          type: 'warning',
          message: 'This activity is Rejected.'
        }
      }
      if (new Date() < dueDate) {
        return {
          editable: true,
          type: 'warning',
          message: 'This activity is Rejected.'
        }
      } else {
        return {
          editable: false,
          type: 'danger',
          message: 'Oops! This activity is rejected and the resubmission request expired.'
        }
      }
    }
  }
  return { editable: false }
}

export function checkAuditorActivityStatus(activity: any, activityType: 'todo' | 'todo_vendor', auditStatus: any) {
  if (!hasUserAccess(USER_PRIVILEGES.REVIEWER_ACTIVITIES_AUDIT)) {
    return { editable: false };
  }
  const { auditted: auditType, status, published } = activity || {};
  if (published) {
    return {
      editable: false,
      message: 'This activity is publised. No modifications allowed. Download audit report for more details.',
      type: 'info',
      alertMessage: "Are you sure you want to <b>Revise and update</b> this activity? This activity is already published."
    }
  }

  if (auditType === ACTIVITY_TYPE.NO_AUDIT) {
    return {
      editable: false,
      type: 'info',
      message: `This activity doesn't required any action.`
    };
  }

  if (auditStatus) {
    const allowedAuditStatuses = [
      AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
      AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
      AUDIT_STATUS.FINAL_AUDIT_EDITING
    ];

    if (allowedAuditStatuses.includes(auditStatus)) {
      return {
        editable: true
      }
    }

    const notAllowedAuditStatuses = [
      AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING,
      AUDIT_STATUS.FINAL_AUDIT_PENDING
    ];
  
    if (notAllowedAuditStatuses.includes(auditStatus)) {
      return {
        editable: false
      }
    }
  }

  if (auditType === ACTIVITY_TYPE.AUDIT) {
    if (status === ACTIVITY_STATUS.AUDITED) {
      return {
        editable: false,
        type: 'success',
        message: activityType == 'todo_vendor' ? 'This Activity is approved, You can still make changes if needed.' : 'This activity is approved. No modifications allowed.',
        alertMessage: "Are you sure you want to make <b>edits and update</b> this activity? This activity is already approved."
      }
    } else if (status === ACTIVITY_STATUS.REJECTED) {
      return {
        editable: true,
        message: 'This activity is rejected and waiting for user confirmation',
        type: 'warning'
      }
    } else if (status === ACTIVITY_STATUS.SUBMITTED) {
      return {
        editable: true
      }
    }
  } else if (auditType === ACTIVITY_TYPE.PHYSICAL_AUDIT) {
    // if (status === ACTIVITY_STATUS.AUDITED) {
    //   return {
    //     editable: false,
    //     type: 'success',
    //     message: 'This activity is approved. No modifications allowed.'
    //   }
    // } else if (status === ACTIVITY_STATUS.REJECTED) {
    //   return {
    //     editable: false,
    //     type: 'danger',
    //     message: 'This activity is rejected. No modifications allowed.'
    //   }
    // } else if (status === ACTIVITY_STATUS.SUBMITTED || status === ACTIVITY_STATUS.OVERDUE) {
    //   return {
    //     editable: true
    //   }
    // }
    return {
      editable: true
    }
  }
  return {
    editable: false
  }
}

export function copyArray(arr: any[]) {
  if ((arr || []).length === 0) {
    return [];
  }
  return JSON.parse(JSON.stringify(arr));
}

export function copyObject(obj: any) {
  if (!obj) {
    return {};
  }
  return JSON.parse(JSON.stringify(obj));
}

export function toBackendDateFormat(date: Date) {
  if (!date) return null;
  // return dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
  return dayjs(date).local().toISOString();
}

export function readBlobAsJson(blob: Blob): Promise<any> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const responseText = reader.result;
      try {
        const responseJson = typeof responseText == 'string' && JSON.parse(responseText);
        resolve(responseJson);
      } catch (err) {
        reject(new Error('Failed to parse JSON from blob'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read blob'));
    };
    reader.readAsText(blob);
  });
}

export function extractTextContent(htmlString: string) {
    const span = document.createElement('span');
    span.innerHTML = htmlString;
    let text;
    if(span.textContent){
      text = span.textContent.replace(/\s+/g,' ').trim();
    } else {
      text= span.innerText.replace(/\s+/g,' ').trim();
    }
    return text || '';
  }
